import { type RaceWithParticipants } from "@/backend/domain/race/getRace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ParticipantTable } from "../participants/participantTable";

type ViewMode = "number" | "class";

interface StartListViewProps {
    race: RaceWithParticipants;
    searchTerm: string;
    viewMode: ViewMode;
}

function filterBySearchTerm(
    participants: RaceWithParticipants["participants"],
    searchTerm: string
) {
    const normalizedSearchTerm = searchTerm.toLowerCase();

    return participants.filter(
        participant =>
            participant.firstName.toLowerCase().includes(normalizedSearchTerm) ||
            participant.lastName.toLowerCase().includes(normalizedSearchTerm)
    );
}

function StartListByNumber({ race, searchTerm }: StartListViewProps) {
    return (
        <ParticipantTable
            participants={filterBySearchTerm(race.participants, searchTerm)}
            showTeam={race.modes.includes("TEAM")}
            showResults={race.status === "COMPLETED"}
            showPlaceOfLiving={race.ask_for_place_of_living}
            showDisciplines={race.allowSnowboard}
            initialSort="startNumber"
        />
    );
}

function StartListByClass({ race, searchTerm }: StartListViewProps) {
    const participantsByClass = race.participants.reduce((acc, participant) => {
        const className = participant.raceClass || "Ohne Klasse";
        if (!acc[className]) {
            acc[className] = [];
        }
        acc[className].push(participant);
        return acc;
    }, {} as Record<string, typeof race.participants>);

    const sortedClasses = Object.keys(participantsByClass).sort((a, b) => {
        if (a === "Ohne Klasse") return 1;
        if (b === "Ohne Klasse") return -1;

        const minA = Math.min(...participantsByClass[a].map(p => p.startNumber ?? Number.MAX_SAFE_INTEGER));
        const minB = Math.min(...participantsByClass[b].map(p => p.startNumber ?? Number.MAX_SAFE_INTEGER));

        return minA - minB;
    });

    return (
        <div className="space-y-8">
            {sortedClasses.map(className => (
                <Card key={className}>
                    <CardHeader>
                        <CardTitle className="text-lg font-light tracking-wide">
                            {className}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ParticipantTable
                            participants={filterBySearchTerm(participantsByClass[className], searchTerm)}
                            showTeam={race.modes.includes("TEAM")}
                            showResults={race.status === "COMPLETED"}
                            groupByClass
                            showPlaceOfLiving={race.ask_for_place_of_living}
                            showDisciplines={race.allowSnowboard}
                            initialSort="startNumber"
                        />
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

export function StartListView({ race, searchTerm, viewMode }: StartListViewProps) {
    if (viewMode === "number") {
        return <StartListByNumber race={race} searchTerm={searchTerm} viewMode={viewMode} />;
    }

    if (viewMode === "class") {
        return <StartListByClass race={race} searchTerm={searchTerm} viewMode={viewMode} />;
    }

    return null;
}
